'use client';

import { useEffect, useState } from 'react';

export default function FeaturedOn() {
    const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);

        const updateTheme = () => {
            const root = document.documentElement;
            const dataTheme = root.getAttribute('data-theme');

            if (dataTheme === 'dark') {
                setCurrentTheme('dark');
            } else if (dataTheme === 'light') {
                setCurrentTheme('light');
            } else {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                setCurrentTheme(prefersDark ? 'dark' : 'light');
            }
        };

        updateTheme();

        const observer = new MutationObserver(updateTheme);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => updateTheme();
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            observer.disconnect();
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    if (!mounted) {
        return null;
    }

    // Use dark badges on light theme (they have black backgrounds)
    // Use dark badges on dark theme too (white text pops)
    const isDark = currentTheme === 'dark';

    return (
        <section className={`section-padding-tight border-y border-[var(--color-md-outline-variant)]/20 ${isDark ? 'glass-card-dark' : 'glass-card'}`}>
            <div className="container mx-auto px-6">
                {/* Minimal Header */}
                <p className="text-center text-sm font-medium mb-14 text-[var(--color-md-on-surface-variant)]">
                    Featured and trusted by
                </p>

                {/* Clean Logo Row - No Cards, No Noise */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-12 sm:gap-24">

                    {/* PeerPush Badge */}
                    <a
                        href="https://peerpush.net/p/wavault"
                        target="_blank"
                        rel="noopener"
                        className="hover:opacity-80 transition-opacity"
                        aria-label="WaVault on PeerPush"
                    >
                        <img
                            src="https://peerpush.net/p/wavault/badge.png"
                            alt="WaVault badge"
                            width={230}
                            height={44}
                            className="h-11 w-auto"
                        />
                    </a>

                    {/* Findly Badge */}
                    <a
                        href="https://findly.tools/whatsapp-contact-extractor-export-group-to-excel-wavault?utm_source=whatsapp-contact-extractor-export-group-to-excel-wavault"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-80 transition-opacity"
                        aria-label="Featured on Findly.tools"
                    >
                        <img
                            src={isDark
                                ? 'https://findly.tools/badges/findly-tools-badge-dark.svg'
                                : 'https://findly.tools/badges/findly-tools-badge-light.svg'
                            }
                            alt="Featured on Findly.tools"
                            width={160}
                            height={44}
                            className="h-11 w-auto"
                        />
                    </a>

                    {/* Chrome Web Store */}
                    <a
                        href="https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                        aria-label="Featured on Chrome Web Store"
                        onClick={() => {
                            if (typeof window !== 'undefined' && (window as any).gtag) {
                                (window as any).gtag('event', 'conversion', {
                                    'send_to': 'AW-17987992395/H28vCO_Sk4QcEMv2q4FD',
                                    'value': 1.0,
                                    'currency': 'INR'
                                });
                            }
                        }}
                    >
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${isDark ? 'bg-[var(--color-md-primary)]/20 text-[var(--color-md-primary)]' : 'bg-[var(--color-md-primary-container)] text-[var(--color-md-on-primary-container)]'}`}>
                            ★ Rated 4.4 (13 ratings)
                        </span>
                        <img
                            src="https://developer.chrome.com/static/docs/webstore/branding/image/mPGKYBIR2uCP0ApchDXE.png"
                            alt="Available in the Chrome Web Store"
                            width={180}
                            height={50}
                            className="h-11 w-auto"
                        />
                    </a>
                </div>
            </div>
        </section>
    );
}
